

import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc'; 
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import updateLocale from 'dayjs/plugin/updateLocale';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import 'dayjs/locale/zh-hk';

export const HK_TZ = "Hongkong";
export const dateFormat = {
  qids: {
    datetime: "YYYY-MM-DDTHH:mm:ssZ",
    datetimeWithMs: "YYYY-MM-DDTHH:mm:ss.SSSZ",
    date: "YYYY-MM-DDZ",
    dateWithoutTimezone: "YYYY-MM-DD",
  },
  cms: {
    datetime: "YYYY-MM-DDTHH:mm:ssZ",
    date: "YYYY-MM-DD"
  },
  params: "YYYYMMDD",
  display: {
    fulldatetime: "DD/MM/YYYY, dddd, HH:mm",
    datetime: "DD/MM/YYYY HH:mm",
    date: "DD/MM/YYYY",
    time: "HH:mm",
  }
}

const dayJS = dayjs;
dayJS.extend(utc);
dayJS.extend(timezone);
dayJS.extend(customParseFormat);
dayJS.extend(isSameOrBefore);
dayJS.extend(isSameOrAfter);
dayJS.extend(updateLocale);
dayJS.updateLocale('en', {
  meridiem: (hour: number, minute: number, isLowercase: boolean) => {
    const m = hour === 12 && minute === 0? 'NOON' : hour < 12 ? 'AM' : 'PM'
    return isLowercase ? m.toLowerCase() : m.toUpperCase()
  }
})
dayJS.updateLocale('zh-hk', {
  meridiem: (hour: number, minute: number) => {
    const m = hour === 12 && minute === 0? '中午' : hour < 12 ? '上午' : '下午'
    return m
  }
})

export const toDayJSWithDefault = (date?: Dayjs | Date | string, sourceFormat?: string[] | string, exactlyMatchSource?: boolean, isHKT?: boolean) => {
  return toDayJS(date, sourceFormat, exactlyMatchSource, isHKT) || dayjs();
}

export const toDayJS = (date?: Dayjs | Date | string, sourceFormat?: string[] | string, exactlyMatchSource?: boolean, isHKT?: boolean) => {
  if (!date) return;
  try {
    switch (typeof date) {
      case 'string':
        // handle date string does not have timezone, prase it in HK time
        if (isHKT) {
          date += '+08:00'
          sourceFormat += 'Z'
        }
        const d = dayJS(date, sourceFormat);
        if (exactlyMatchSource) {
          if (!sourceFormat || sourceFormat?.length === 0) {
            throw "missing source format";
          }
          return d;
        }
        return d.isValid() ? d : dayJS(date);
      default:
        return dayJS(date);
    }
  } catch (err) {
    console.log('toDayJS() params: ', {date: date, dateType: typeof date, sourceFormat: sourceFormat}, '\nerror:', err)
  }
}

export const toDate = (date?: string, sourceFormat?: string[] | string, isHKT?: boolean) => {
  return toDayJS(date, sourceFormat, undefined, isHKT)?.toDate();
}

export const isValidDate = (date: string | undefined, sourceFormat: string) => {
  return toDayJS(date, sourceFormat, true)?.isValid();
}

/** 
 * Convert date/ date string to target format. Return empty string if undefined.
 * @param date - Date/string
 * @param targetFormat - Display in the way you like (refer to day.js for more information: https://day.js.org/docs/en/display/format)
 * @param sourceFormat - The source format of date string (refer to day.js for more information)
 * @param exactlyMatchSource - Set if need to exactly match with source format, use with sourceFormat
 * @param utc - Set if need to display in UTC
 */ 
export const formattedDate = (
  date: Dayjs | Date | undefined,
  targetFormat: string,
  utc?: boolean,
) => {
  const d = toDayJS(date)
  return (utc ? d?.utc().format(targetFormat) : d?.tz(HK_TZ).format(targetFormat)) || '';
};

export const compareDate = (date1?: string, date2?: string, compareFlag?: "after" | "before", sameAs: boolean = true): boolean => {
  if (compareFlag == "after") {
    return sameAs ? dayJS(date1).isSameOrAfter(date2) : dayJS(date1).isAfter(date2)
  }
  else if (compareFlag == "before") {
    return sameAs ? dayJS(date1).isSameOrBefore(date2) : dayJS(date1).isBefore(date2)
  }
  else {
    return sameAs ? dayJS(date1).isSameOrAfter(date2) : dayJS(date1).isAfter(date2)
  }
}

export const minDate = (date1?: Date, date2?: Date) => {
  if (date1 && date2){
    return new Date(Math.min(date1.getTime(), date2.getTime()))
  }
  return date1 ? date1 : date2;
}

export const withinDate = (minDate?: Date, maxDate?: Date, dateForCompare?: Date) => {
  const isAfterMinDate = dateForCompare?.getTime() ? dayJS(dateForCompare).isSameOrAfter(minDate) : false;
  const isBeforeMaxDate= dateForCompare?.getTime() ? dayJS(dateForCompare).isSameOrBefore(maxDate) : false;
  return isAfterMinDate && isBeforeMaxDate;
}

export default dayJS;